import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, axiosInstance as axios } from '../context/AuthContext';
import { FaBell, FaCheck, FaTrash, FaArrowLeft, FaCheckDouble } from 'react-icons/fa';

const API = '';

function Notifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/api/notifications?limit=100`, {
        withCredentials: true
      });
      setNotifications(res.data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/api/notifications/${notificationId}/read`, {}, {
        withCredentials: true
      });
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/api/notifications/read-all`, {}, {
        withCredentials: true
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/api/notifications/${notificationId}`, {
        withCredentials: true
      });
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      'family_invite': '👨‍👩‍👧‍👦',
      'event_created': '📅',
      'event_reminder': '⏰',
      'message': '💬',
      'dues_reminder': '💰',
      'member_joined': '🎉',
      'photo_uploaded': '📷',
      'default': '🔔'
    };
    return icons[type] || icons.default;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="notifications-page">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition"
                data-testid="back-btn"
              >
                <FaArrowLeft className="text-xl" />
              </button>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <FaBell className="text-blue-600 text-xl" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Notifications</h1>
                  <p className="text-sm text-gray-500">
                    {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
                  </p>
                </div>
              </div>
            </div>
            
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center space-x-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                data-testid="mark-all-read-btn"
              >
                <FaCheckDouble />
                <span>Mark all as read</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-6">
        {notifications.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaBell className="text-gray-400 text-3xl" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No notifications yet</h2>
            <p className="text-gray-500">
              When you receive notifications, they'll appear here.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="divide-y divide-gray-100">
              {notifications.map(notification => (
                <div
                  key={notification.notification_id}
                  className={`p-4 hover:bg-gray-50 transition ${!notification.read ? 'bg-blue-50/50' : ''}`}
                  data-testid={`notification-${notification.notification_id}`}
                >
                  <div className="flex items-start space-x-4">
                    <div className="text-2xl flex-shrink-0">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-gray-900 ${!notification.read ? 'font-semibold' : ''}`}>
                        {notification.title || notification.message}
                      </p>
                      {notification.title && notification.message && (
                        <p className="text-gray-600 text-sm mt-1">{notification.message}</p>
                      )}
                      <p className="text-gray-400 text-xs mt-2">
                        {formatDate(notification.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      {!notification.read && (
                        <button
                          onClick={() => markAsRead(notification.notification_id)}
                          className="flex items-center gap-1 px-2 py-1.5 sm:p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition text-xs sm:text-sm"
                          title="Mark as read"
                          data-testid={`mark-read-${notification.notification_id}`}
                        >
                          <FaCheck />
                          <span className="sm:hidden">Read</span>
                        </button>
                      )}
                      <button
                        onClick={() => deleteNotification(notification.notification_id)}
                        className="flex items-center gap-1 px-2 py-1.5 sm:p-2 text-red-500 hover:bg-red-100 rounded-lg transition text-xs sm:text-sm"
                        title="Delete"
                        data-testid={`delete-${notification.notification_id}`}
                      >
                        <FaTrash />
                        <span className="sm:hidden">Delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Notifications;
